import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiMessageSquare, FiSearch, FiPlus, FiUser, FiClock } from 'react-icons/fi';
import { communityAPI } from '../../service/api';
import { useAuth } from '../../contexts/AuthContext';
import { showError } from '../../service/swal';
import PageMeta from '../../components/PageMeta';
import useAutoRefresh from '../../hooks/useAutoRefresh';

export default function CommunityPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const limit = 20;

  const loadPosts = async (skipLoading = false) => {
    if (!skipLoading) setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      const res = await communityAPI.getAll(params);
      setPosts(res.data.data.posts);
      setTotal(res.data.data.total);
    } catch (err) {
      showError('Erreur', 'Impossible de charger les publications');
    } finally {
      if (!skipLoading) setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, [page]);
  useAutoRefresh(() => loadPosts(true), 30000);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    if (search) {
      setSearchParams({ search });
    } else {
      setSearchParams({});
    }
    loadPosts();
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) {
      const hours = Math.floor(diff / (1000 * 60 * 60));
      if (hours === 0) return 'À l\'instant';
      return `Il y a ${hours}h`;
    }
    if (days === 1) return 'Hier';
    if (days < 7) return `Il y a ${days} jours`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="page-container py-8">
      <PageMeta title="Communauté" description="Forum d'échange pour étudiants internationaux à l'UNIKOL" />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold" style={{ color: '#0A192F' }}>
            Communauté
          </h1>
          <p className="mt-1 text-sm" style={{ color: '#7A828A' }}>
            Échangez avec d'autres étudiants internationaux
          </p>
        </div>
        {user && (
          <Link
            to="/communautes/nouveau"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold tracking-wider uppercase"
            style={{ backgroundColor: '#C59B6C', color: '#0A192F' }}
          >
            <FiPlus className="w-4 h-4" />
            Nouvelle publication
          </Link>
        )}
      </div>

      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative max-w-md">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#88929B' }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher dans la communauté..."
            className="w-full pl-10 pr-4 py-2.5 text-sm border"
            style={{ borderColor: '#E2E2E0', color: '#0A192F', borderRadius: 0 }}
          />
        </div>
      </form>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-t-transparent mx-auto" style={{ borderColor: '#C59B6C', borderTopColor: 'transparent', borderRadius: '50%' }} />
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12">
          <FiMessageSquare className="w-12 h-12 mx-auto mb-4" style={{ color: '#88929B' }} />
          <p className="text-lg font-medium" style={{ color: '#0A192F' }}>Aucune publication</p>
          <p className="text-sm mt-1" style={{ color: '#7A828A' }}>
            {search ? 'Aucun résultat pour votre recherche' : 'Soyez le premier à publier !'}
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {posts.map((post) => (
              <Link
                key={post.id}
                to={`/communautes/${post.id}`}
                className="block card-root p-5 hover:shadow-md transition-shadow"
              >
                <h2 className="font-serif text-lg font-bold" style={{ color: '#0A192F' }}>
                  {post.titre}
                </h2>
                <p className="mt-2 text-sm line-clamp-2" style={{ color: '#7A828A' }}>
                  {post.contenu}
                </p>
                <div className="mt-3 flex items-center gap-4 text-xs" style={{ color: '#88929B' }}>
                  <span className="flex items-center gap-1">
                    <FiUser className="w-3.5 h-3.5" />
                    {post.auteurNom}
                  </span>
                  <span className="flex items-center gap-1">
                    <FiClock className="w-3.5 h-3.5" />
                    {formatDate(post.dateCreation)}
                  </span>
                  <span className="flex items-center gap-1">
                    <FiMessageSquare className="w-3.5 h-3.5" />
                    {post.commentCount} commentaire{post.commentCount > 1 ? 's' : ''}
                  </span>
                  {post.tags && (
                    <span className="ml-auto hidden sm:inline" style={{ color: '#C59B6C' }}>
                      #{post.tags.split(',').join(' #')}
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="mt-8 flex justify-center gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className="w-9 h-9 text-sm font-medium border"
                  style={{
                    backgroundColor: p === page ? '#0A192F' : 'transparent',
                    color: p === page ? '#FFFFFF' : '#0A192F',
                    borderColor: '#E2E2E0',
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
